import {
  parts
} from "./chunk-BJXKBZJG.mjs";
import {
  dom
} from "./chunk-Y7IZ6OAH.mjs";

// ../../utilities/dom/src/attrs.ts
var dataAttr = (guard) => {
  return guard ? "" : void 0;
};
var ariaAttr = (guard) => {
  return guard ? "true" : void 0;
};

// ../../utilities/core/src/warning.ts
function invariant(...a) {
  const m = a.length === 1 ? a[0] : a[1];
  const c = a.length === 2 ? a[0] : true;
  if (c && process.env.NODE_ENV !== "production") {
    throw new Error(m);
  }
}

// ../../utilities/dom/src/event.ts
function getNativeEvent(e) {
  return e.nativeEvent ?? e;
}
var isModifiedEvent = (v) => v.ctrlKey || v.altKey || v.metaKey;

// ../../utilities/dom/src/keyboard-event.ts
var rtlKeyMap = {
  ArrowLeft: "ArrowRight",
  ArrowRight: "ArrowLeft"
};
var sameKeyMap = {
  Up: "ArrowUp",
  Down: "ArrowDown",
  Esc: "Escape",
  " ": "Space",
  ",": "Comma",
  Left: "ArrowLeft",
  Right: "ArrowRight"
};
function getEventKey(event, options = {}) {
  const { dir = "ltr", orientation = "horizontal" } = options;
  let { key } = event;
  key = sameKeyMap[key] ?? key;
  const isRtl = dir === "rtl" && orientation === "horizontal";
  if (isRtl && key in rtlKeyMap) {
    key = rtlKeyMap[key];
  }
  return key;
}

// ../../utilities/dom/src/visually-hidden.ts
var visuallyHiddenStyle = {
  border: "0",
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: "0",
  position: "absolute",
  width: "1px",
  whiteSpace: "nowrap",
  wordWrap: "normal"
};

// src/pin-input.connect.ts
function connect(state, send, normalize) {
  const isValueComplete = state.context.isValueComplete;
  const isInvalid = state.context.invalid;
  const focusedIndex = state.context.focusedIndex;
  const translations = state.context.translations;
  function focus() {
    dom.getFirstInputEl(state.context)?.focus();
  }
  return {
    value: state.context.value,
    valueAsString: state.context.valueAsString,
    isValueComplete,
    setValue(value) {
      if (!Array.isArray(value)) {
        invariant("[pin-input/setValue] value must be an array");
      }
      send({ type: "SET_VALUE", value });
    },
    clearValue() {
      send({ type: "CLEAR_VALUE" });
    },
    setValueAtIndex(index, value) {
      send({ type: "SET_VALUE", value, index });
    },
    focus,
    rootProps: normalize.element({
      dir: state.context.dir,
      ...parts.root.attrs,
      id: dom.getRootId(state.context),
      "data-invalid": dataAttr(isInvalid),
      "data-disabled": dataAttr(state.context.disabled),
      "data-complete": dataAttr(isValueComplete)
    }),
    labelProps: normalize.label({
      ...parts.label.attrs,
      htmlFor: dom.getHiddenInputId(state.context),
      id: dom.getLabelId(state.context),
      "data-invalid": dataAttr(isInvalid),
      "data-disabled": dataAttr(state.context.disabled),
      "data-complete": dataAttr(isValueComplete),
      onClick: (event) => {
        event.preventDefault();
        focus();
      }
    }),
    hiddenInputProps: normalize.input({
      ...parts.hiddenInput.attrs,
      "aria-hidden": true,
      type: "text",
      tabIndex: -1,
      id: dom.getHiddenInputId(state.context),
      name: state.context.name,
      form: state.context.form,
      style: visuallyHiddenStyle,
      maxLength: state.context.valueLength,
      defaultValue: state.context.valueAsString
    }),
    controlProps: normalize.element({
      ...parts.control.attrs,
      id: dom.getControlId(state.context)
    }),
    getInputProps({ index }) {
      const inputType = state.context.type === "numeric" ? "tel" : "text";
      return normalize.input({
        ...parts.input.attrs,
        disabled: state.context.disabled,
        "data-disabled": dataAttr(state.context.disabled),
        "data-complete": dataAttr(isValueComplete),
        id: dom.getInputId(state.context, index.toString()),
        "data-ownedby": dom.getRootId(state.context),
        "aria-label": translations.inputLabel(index, state.context.valueLength),
        inputMode: state.context.otp || state.context.type === "numeric" ? "numeric" : "text",
        "aria-invalid": ariaAttr(isInvalid),
        "data-invalid": dataAttr(isInvalid),
        type: state.context.mask ? "password" : inputType,
        defaultValue: state.context.value[index] || "",
        autoCapitalize: "none",
        autoComplete: state.context.otp ? "one-time-code" : "off",
        placeholder: focusedIndex === index ? "" : state.context.placeholder,
        onChange(event) {
          const evt = getNativeEvent(event);
          const { value } = event.currentTarget;
          if (evt.inputType === "insertFromPaste" || value.length > 2) {
            send({ type: "PASTE", value });
            event.preventDefault();
            return;
          }
          if (evt.inputType === "deleteContentBackward") {
            send("BACKSPACE");
            return;
          }
          send({ type: "INPUT", value, index });
        },
        onKeyDown(event) {
          const evt = getNativeEvent(event);
          if (evt.isComposing || isModifiedEvent(evt))
            return;
          const keyMap = {
            Backspace() {
              send("BACKSPACE");
            },
            Delete() {
              send("DELETE");
            },
            ArrowLeft() {
              send("ARROW_LEFT");
            },
            ArrowRight() {
              send("ARROW_RIGHT");
            },
            Enter() {
              send("ENTER");
            }
          };
          const key = getEventKey(event, { dir: state.context.dir });
          const exec = keyMap[key];
          if (exec) {
            exec(event);
            event.preventDefault();
          } else {
            if (key === "Tab")
              return;
            send({ type: "KEY_DOWN", value: key, preventDefault: () => event.preventDefault() });
          }
        },
        onFocus() {
          send({ type: "FOCUS", index });
        },
        onBlur() {
          send({ type: "BLUR", index });
        }
      });
    }
  };
}

export {
  connect
};
